import { useEffect, useState, useRef } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

/**
 * Full-screen image viewer with pinch / wheel / double-tap zoom + pan, and
 * left/right swipe (or arrow keys / arrow buttons) to navigate between photos.
 *
 * Two usage modes:
 *  - Single image:  <PhotoLightbox src={url} onClose={...} />
 *  - Gallery:       <PhotoLightbox photos={[{url, alt}, ...]} index={0} onIndexChange={...} onClose={...} />
 */
export const PhotoLightbox = ({ src, alt, photos, index = 0, onIndexChange, onClose }) => {
  // Normalise to a gallery list internally. `src` mode → single-item gallery.
  const list = photos && photos.length ? photos : (src ? [{ url: src, alt }] : []);
  const isOpen = list.length > 0;

  const [current, setCurrent] = useState(index);
  // Keep `current` in sync with externally controlled index when the parent
  // re-opens the lightbox on a different photo.
  useEffect(() => { if (isOpen) setCurrent(index); }, [index, isOpen]);

  const goTo = (next) => {
    if (next < 0 || next >= list.length) return;
    setCurrent(next);
    if (onIndexChange) onIndexChange(next);
  };
  const prev = () => goTo(current - 1);
  const next = () => goTo(current + 1);

  // Keyboard nav
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, current, list.length]);

  // Touch swipe nav — only fires when the image isn't zoomed in (scale ~= 1).
  // We track touchstart x and compare on touchend; threshold 50 px.
  const touchStartX = useRef(null);
  const scaleRef = useRef(1);
  const onTouchStart = (e) => {
    if (scaleRef.current > 1.05) { touchStartX.current = null; return; }
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) < 50) return;
    if (dx > 0) prev(); else next();
  };

  if (!isOpen) return null;
  const photo = list[current] || list[0];

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
      data-testid="photo-lightbox"
      role="dialog"
      aria-label="Enlarged photo"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 z-20 h-11 w-11 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors"
        aria-label="Close"
        data-testid="photo-lightbox-close"
      >
        <X className="h-5 w-5 text-white" />
      </button>

      {list.length > 1 && (
        <>
          <button
            type="button"
            onClick={prev}
            disabled={current === 0}
            className="absolute left-3 top-1/2 -translate-y-1/2 z-20 h-11 w-11 bg-white/10 hover:bg-white/20 disabled:opacity-25 disabled:cursor-not-allowed rounded-full flex items-center justify-center transition-colors"
            aria-label="Previous photo"
            data-testid="photo-lightbox-prev"
          >
            <ChevronLeft className="h-6 w-6 text-white" />
          </button>
          <button
            type="button"
            onClick={next}
            disabled={current === list.length - 1}
            className="absolute right-3 top-1/2 -translate-y-1/2 z-20 h-11 w-11 bg-white/10 hover:bg-white/20 disabled:opacity-25 disabled:cursor-not-allowed rounded-full flex items-center justify-center transition-colors"
            aria-label="Next photo"
            data-testid="photo-lightbox-next"
          >
            <ChevronRight className="h-6 w-6 text-white" />
          </button>
        </>
      )}

      <TransformWrapper
        key={current}                 /* reset zoom when switching photos */
        initialScale={1}
        minScale={1}
        maxScale={6}
        doubleClick={{ mode: 'toggle', step: 2 }}
        wheel={{ step: 0.2 }}
        pinch={{ step: 5 }}
        centerOnInit
        onTransformed={(_, state) => { scaleRef.current = state.scale; }}
      >
        <TransformComponent
          wrapperStyle={{ width: '100vw', height: '100vh' }}
          contentStyle={{ width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <img
            src={photo.url}
            alt={photo.alt || ''}
            className="max-w-full max-h-full object-contain select-none"
            draggable={false}
          />
        </TransformComponent>
      </TransformWrapper>

      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 pointer-events-none">
        {photo.caption && (
          <p className="text-white text-sm font-medium px-3 py-1 bg-black/40 rounded">
            {photo.caption}
          </p>
        )}
        <p className="text-[11px] text-white/60 uppercase tracking-wider">
          {list.length > 1 ? `${current + 1} / ${list.length} · ` : ''}Pinch · swipe · double-tap to zoom
        </p>
      </div>
    </div>
  );
};

export default PhotoLightbox;
